import React from 'react';
import dayjs from 'dayjs';
import { 
    FileImageOutlined, 
    VideoCameraOutlined, 
    FileTextOutlined, 
    AudioOutlined, 
    ContactsOutlined, 
    EnvironmentOutlined, 
    AppstoreOutlined 
} from '@ant-design/icons';

export interface ChatLogPayload {
    id: number | string;
    messageType: 'UI' | 'BI';
    createdAt: string;
    message: any;
}

interface WhatsAppChatProps {
    logs: ChatLogPayload[];
    wabaName?: string;
    userName?: string;
    isLoading?: boolean;
}

export interface ParsedContent {
    text?: string;
    media?: {
        type: 'image' | 'video' | 'audio' | 'document' | 'sticker';
        caption?: string;
        link?: string;
        filename?: string;
    };
    location?: {
        name?: string;
        address?: string;
        lat: number;
        long: number;
    };
    contacts?: Array<{ name: string; phones: string[] }>;
    cards?: Array<{
        image?: string; 
        title?: string;
        body?: string;
        buttons?: string[];
    }>;
    options?: string[]; // for simple list/buttons
    templateName?: string;
}

/** Helper function to parse WhatsApp API payload (both Webhook and Raw API format) */
export const parseWhatsAppPayload = (payloadStr: any): ParsedContent[] => {
    try {
        const parseJson = (value: any): any => {
            let parsed = value;

            // Some logging records contain JSON encoded more than once.
            for (let i = 0; i < 3 && typeof parsed === 'string'; i += 1) {
                try {
                    const next = JSON.parse(parsed);
                    if (next === parsed) break;
                    parsed = next;
                } catch {
                    break;
                }
            }

            return parsed;
        };

        const payload = parseJson(payloadStr);
        let msgs: any[] = [];

        // The logging API may serialize `value.messages` independently of the
        // outer webhook payload. Normalize arrays, objects and JSON strings so
        // all of them follow the same rendering path.
        const normalizeMessages = (messages: any, fallbackType?: string): any[] => {
            const parsedMessages = parseJson(messages);

            if (Array.isArray(parsedMessages)) {
                return parsedMessages.flatMap(message => normalizeMessages(message, fallbackType));
            }

            if (parsedMessages && typeof parsedMessages === 'object') {
                // Handle a nested logging shape such as { messages: [...] }.
                if (parsedMessages.messages !== undefined) {
                    return normalizeMessages(
                        parsedMessages.messages,
                        parsedMessages.type || parsedMessages.message_type || parsedMessages.messageType || fallbackType
                    );
                }

                return [parsedMessages];
            }

            // PII processing can leave the body as a plain string while the
            // message type remains on the parent logging object.
            if (typeof parsedMessages === 'string' && parsedMessages.trim()) {
                return [{
                    type: fallbackType || 'text',
                    text: { body: parsedMessages },
                }];
            }

            return [];
        };

        // 1. Check if it's a Meta Webhook format
        if (payload?.entry?.[0]?.changes?.[0]?.value) {
            const value = payload.entry[0].changes[0].value;
            if (value.messages) {
                msgs = normalizeMessages(
                    value.messages,
                    value.type || value.message_type || value.messageType
                );
            } else if (value.statuses && value.statuses.length > 0) {
                const statusInfo = value.statuses[0];
                return [{ text: `[Message status update: ${statusInfo.status}]` }];
            }
        } 
        // 2. Some logging responses expose the message at the top level.
        else if (Array.isArray(payload)) {
            msgs = normalizeMessages(payload);
        }
        else if (payload?.messages !== undefined || payload?.message !== undefined) {
            msgs = normalizeMessages(
                payload.messages !== undefined ? payload.messages : payload.message,
                payload.type || payload.message_type || payload.messageType
            );
        }
        // 3. Check if it's an API request payload (Business Initiated raw payload)
        else if (payload?.messaging_product === 'whatsapp' || payload?.type) {
            msgs = normalizeMessages(payload.messages || payload);
        }

        if (msgs.length === 0) return [{ text: 'Unrecognized message format' }];

        return msgs.map((msg: any) => {
            // Conversation logging can wrap the WhatsApp message as
            // { message_type, message }. Unwrap that form before parsing it.
            const nestedMessage = parseJson(msg.message);
            const whatsappTypes = ['text', 'image', 'video', 'document', 'audio', 'sticker', 'location', 'contacts', 'interactive', 'template'];
            const declaredType = msg.type || msg.message_type || msg.messageType;
            const nestedType = nestedMessage?.type || nestedMessage?.message_type || nestedMessage?.messageType;
            const inferredType = nestedMessage?.text ? 'text'
                : nestedMessage?.location ? 'location'
                    : nestedMessage?.contacts ? 'contacts'
                        : nestedMessage?.interactive ? 'interactive'
                            : nestedMessage?.template ? 'template'
                                : undefined;
            const type = whatsappTypes.includes(declaredType)
                ? declaredType
                : whatsappTypes.includes(nestedType)
                    ? nestedType
                    : inferredType
                        ? inferredType
                        : (nestedMessage && typeof nestedMessage === 'object')
                            ? undefined
                            : 'text';
            const normalizedMessage = nestedType ? nestedMessage : msg;
            const result: ParsedContent = {};

            if (type === 'text') {
                result.text = normalizedMessage.text?.body || ''; // webhook format
                if (!result.text && typeof normalizedMessage.text === 'string') result.text = normalizedMessage.text; // raw format sometimes
                if (!result.text && typeof msg.message === 'string') result.text = msg.message; // logging wrapper format
            } 
            else if (['image', 'video', 'document', 'audio', 'sticker'].includes(type)) {
                const mediaObj = normalizedMessage[type] || {};
                result.media = {
                    type: type as any,
                    caption: mediaObj.caption || '',
                    link: mediaObj.link || mediaObj.url || '',
                    filename: mediaObj.filename || ''
                };
            } 
            else if (type === 'location') {
                const loc = normalizedMessage.location || {};
                result.location = {
                    name: loc.name,
                    address: loc.address,
                    lat: loc.latitude,
                    long: loc.longitude
                };
            } 
            else if (type === 'contacts') {
                const contacts = normalizedMessage.contacts || [];
                result.contacts = contacts.map((c: any) => ({
                    name: c.name?.formatted_name || c.name?.first_name || 'Contact',
                    phones: c.phones?.map((p: any) => p.phone) || []
                }));
            } 
            else if (type === 'interactive') {
                const interactive = normalizedMessage.interactive || {};
                const intType = interactive.type;

                if (intType === 'list') {
                    result.text = interactive.body?.text || 'Interactive List';
                    result.options = interactive.action?.sections?.[0]?.rows?.map((r: any) => r.title || r.id) || [];
                } else if (intType === 'button') {
                    result.text = interactive.body?.text || 'Interactive Button';
                    result.options = interactive.action?.buttons?.map((b: any) => b.reply?.title || b.title) || [];
                } else if (intType === 'carousel') {
                    result.text = interactive.body?.text || '';
                    result.cards = interactive.action?.cards?.map((card: any) => {
                        const headerType = card.header?.type;
                        const headerData = headerType ? card.header[headerType] : null;
                        return {
                            image: headerData?.link || headerData?.url,
                            title: card.body?.text || 'Card',
                            buttons: card.action?.buttons?.map((b: any) => b.reply?.title || b.name) || []
                        };
                    }) || [];
                } else if (intType === 'list_reply') {
                    result.text = interactive.list_reply?.title || interactive.list_reply?.description || '';
                } else if (intType === 'button_reply') {
                    result.text = interactive.button_reply?.title || '';
                } else {
                    result.text = `[Interactive type: ${intType}]`;
                }
            } 
            else if (type === 'template') {
                const template = normalizedMessage.template || {};
                result.templateName = template.name;
                const components = template.components || [];
                const bodyComp = components.find((c: any) => c.type === 'body');
                const headerComp = components.find((c: any) => c.type === 'header');
                
                if (bodyComp?.parameters) {
                    result.text = `[Template: ${template.name}]\nVariables: ` + bodyComp.parameters.map((p:any) => p.text || p.type).join(', ');
                } else if (template.name) {
                    result.text = `[Template Message: ${template.name}]`;
                }

                if (headerComp?.parameters) {
                     const mediaParam = headerComp.parameters.find((p:any) => ['image','video','document'].includes(p.type));
                     if (mediaParam) {
                          const mType = mediaParam.type;
                          result.media = {
                              type: mType as any,
                              link: mediaParam[mType]?.link || mediaParam[mType]?.url || ''
                          };
                     }
                }
                
                const buttonComps = components.filter((c: any) => c.type === 'button');
                if (buttonComps.length > 0) {
                     result.options = buttonComps.flatMap((c: any) => c.parameters?.map((p:any) => p.text || p.payload) || []);
                }
            }
            else {
                result.text = type
                    ? `[Unsupported message type: ${type}]`
                    : 'Message content unavailable';
            }

            return result;
        });

    } catch (e) {
        console.error('Error parsing WhatsApp Payload', e);
        return [{ text: 'Error parsing message' }];
    }
};

const WhatsAppChat: React.FC<WhatsAppChatProps> = ({ logs, wabaName = 'Bot', userName = 'User', isLoading = false }) => {
    // Sort logs by created_at ascending (oldest to newest)
    const sortedLogs = [...logs].sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());

    const renderMedia = (media: ParsedContent['media']) => {
        if (!media) return null;
        
        let Icon = FileTextOutlined;
        let label = "File";
        if (media.type === 'image') { Icon = FileImageOutlined; label = "Image"; }
        if (media.type === 'video') { Icon = VideoCameraOutlined; label = "Video"; }
        if (media.type === 'audio') { Icon = AudioOutlined; label = "Audio"; }
        if (media.type === 'document' || media.type === 'sticker') { Icon = FileTextOutlined; label = media.type === 'sticker' ? "Sticker" : "Document"; }

        return (
            <div className="flex flex-col gap-1 mb-2 max-w-full">
                {media.link ? (
                    media.type === 'image' ? (
                        <img src={media.link} alt="media" className="rounded-lg max-h-48 object-cover bg-gray-100" />
                    ) : media.type === 'video' ? (
                         <video src={media.link} controls className="rounded-lg max-h-48 bg-black w-full" />
                    ) : (
                        <div className="flex items-center gap-3 bg-black/5 p-3 rounded-lg border border-gray-200">
                            <Icon className="text-2xl text-gray-500" />
                            <div className="flex-1 overflow-hidden">
                                <div className="font-medium text-sm truncate">{media.filename || `${label} attached`}</div>
                                <a href={media.link} target="_blank" rel="noreferrer" className="text-xs text-blue-500 truncate block hover:underline">Download</a>
                            </div>
                        </div>
                    )
                ) : (
                    <div className="flex items-center gap-3 bg-black/5 p-4 rounded-lg border border-gray-200 justify-center flex-col aspect-video">
                        <Icon className="text-4xl text-gray-400" />
                        <span className="text-xs text-gray-500 font-medium mt-2">{label} details received</span>
                        {media.filename && <span className="text-[10px] text-gray-400 text-center px-2">{media.filename}</span>}
                    </div>
                )}
                {media.caption && (
                    <div className="text-sm mt-1 whitespace-pre-wrap">{media.caption}</div>
                )}
            </div>
        );
    };

    const renderLocation = (loc: ParsedContent['location']) => {
        if (!loc) return null;
        return (
            <div className="flex items-start gap-2 bg-black/5 p-3 rounded-lg border border-gray-200 mb-2">
                <EnvironmentOutlined className="text-xl text-red-500 mt-1" />
                <div className="flex-1 overflow-hidden">
                    <div className="font-medium text-sm text-[#111111]">{loc.name || 'Location'}</div>
                    {loc.address && <div className="text-xs text-gray-600 truncate">{loc.address}</div>}
                    <div className="text-[10px] text-gray-400 mt-1">
                        Lat: {loc.lat}, Long: {loc.long}
                    </div>
                </div>
            </div>
        );
    };

    const renderContacts = (contacts: ParsedContent['contacts']) => {
        if (!contacts || contacts.length === 0) return null;
        return (
            <div className="flex flex-col gap-1 mb-2">
                {contacts.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 bg-black/5 p-3 rounded-lg border border-gray-200">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <ContactsOutlined className="text-gray-500 text-lg" />
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <div className="font-medium text-sm text-[#111111] truncate">{c.name}</div>
                            {c.phones.map((p, idx) => (
                                <div key={idx} className="text-xs text-blue-500 truncate">{p}</div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const renderCarousel = (cards: ParsedContent['cards']) => {
        if (!cards || cards.length === 0) return null;
        return (
            <div className="flex gap-2 overflow-x-auto pb-2 snap-x mt-2 w-full max-w-[260px] custom-scrollbar">
                {cards.map((card, i) => (
                    <div key={i} className="flex-none w-[200px] bg-white rounded-lg border border-gray-200 overflow-hidden text-wrap flex flex-col snap-center">
                        {card.image ? (
                            <div className="w-full h-24 bg-gray-100 flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `url(${card.image})` }}>
                            </div>
                        ) : (
                            <div className="w-full h-24 bg-gray-100 flex items-center justify-center">
                                <FileImageOutlined className="text-3xl text-gray-300" />
                            </div>
                        )}
                        <div className="p-2 flex-1 flex flex-col justify-between">
                            <div className="text-[13px] font-medium text-[#111111] line-clamp-3 leading-tight">{card.title}</div>
                        </div>
                        {card.buttons && card.buttons.length > 0 && (
                            <div className="flex flex-col border-t border-gray-100">
                                {card.buttons.map((btn, idx) => (
                                    <div key={idx} className="text-[#00a884] text-center text-[13px] py-1.5 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-0 font-medium">
                                        {btn}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-[#EFEAE2] min-h-[500px] border border-gray-200 relative overflow-hidden font-sans">
            {/* WhatsApp Background pattern overlay */}
            <div
                className="absolute inset-0 opacity-[0.06] pointer-events-none z-0"
                style={{
                    backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '350px'
                }}
            />

            {/* Header */}
            <div className="bg-[#005c4b] text-white px-4 py-2.5 flex items-center shadow-[0_1px_3px_rgba(0,0,0,0.1)] z-10 w-full relative">
                <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-black font-bold mr-3 overflow-hidden">
                    <img src="https://cdn-icons-png.flaticon.com/512/4712/4712109.png" alt="bot" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 overflow-hidden">
                    <div className="font-semibold text-base truncate leading-tight">{wabaName}</div>
                    <div className="text-[11px] text-white/80 font-medium">Business Account</div>
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2 z-10 w-full">
                {isLoading ? (
                    <div className="text-center text-gray-500 bg-white/80 rounded-lg p-2 mx-auto mt-4 shadow-sm text-sm">
                        Loading conversation...
                    </div>
                ) : sortedLogs.length === 0 ? (
                    <div className="text-center text-gray-500 bg-white/80 rounded-lg p-3 mx-auto mt-4 shadow-sm text-sm px-5">
                        <span className="block mb-1">📅</span>
                        No conversation history found.
                    </div>
                ) : null}

                {sortedLogs.map((log, index) => {
                    const isUser = log.messageType === 'UI';
                    const contents = parseWhatsAppPayload(log.message);
                    const time = dayjs(log.createdAt).format('HH:mm');

                    return contents.map((content, cIndex) => (
                        <div key={`${log.id || index}-${cIndex}`} className={`flex w-full mb-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
                            <div
                                className={`relative max-w-[85%] px-[10px] py-[6px] shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] text-[14.2px] leading-[19px] flex flex-col ${isUser
                                    ? `bg-[#d9fdd3] rounded-lg ${cIndex === 0 ? 'rounded-tr-none' : ''}`
                                    : `bg-white rounded-lg ${cIndex === 0 ? 'rounded-tl-none' : ''}`
                                    }`}
                            >
                                {/* Tail SVG simulation via pure CSS or SVG */}
                                {cIndex === 0 && (
                                    isUser ? (
                                        <svg viewBox="0 0 8 13" width="8" height="13" className="absolute top-0 -right-[8px] text-[#d9fdd3]">
                                            <path opacity=".13" fill="#0000000" d="M1.533 3.153L.085 1.58A1.5 1.5 0 0 1 0 1.053v11.691L1.533 3.153z"></path>
                                            <path fill="currentColor" d="M1.533 3.153L.085 1.58A1.5 1.5 0 0 1 0 1.053V0h7.502a1.5 1.5 0 0 1 1.06.44L1.533 3.153z"></path>
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 8 13" width="8" height="13" className="absolute top-0 -left-[8px] text-white">
                                            <path opacity=".13" fill="#0000000" d="M1.533 3.153L.085 1.58A1.5 1.5 0 0 1 0 1.053v11.691L1.533 3.153z"></path>
                                            <path fill="currentColor" d="M1.533 3.153L.085 1.58A1.5 1.5 0 0 1 0 1.053V0h7.502a1.5 1.5 0 0 1 1.06.44L1.533 3.153z"></path>
                                        </svg>
                                    )
                                )}

                                {/* Content Rendering */}
                                {content.templateName && !content.text && !content.media && (
                                    <div className="flex items-center gap-2 text-[12px] text-gray-500 border-b border-gray-100 pb-1 mb-1 font-medium italic">
                                        <AppstoreOutlined /> Template: {content.templateName}
                                    </div>
                                )}
                                
                                {renderMedia(content.media)}
                                {renderLocation(content.location)}
                                {renderContacts(content.contacts)}
                                
                                {content.text && (
                                    <div className="text-[#111111] whitespace-pre-wrap word-break">
                                        {content.text}
                                    </div>
                                )}

                                {renderCarousel(content.cards)}

                                {/* Options if bot sent a list or buttons */}
                                {content.options && content.options.length > 0 && (
                                    <div className="mt-2 flex flex-col gap-1 border-t border-gray-100 pt-2 pb-1">
                                        {content.options.map((opt: string, i: number) => (
                                            <div key={i} className="text-[#00a884] text-center text-[14.5px] py-1 cursor-pointer hover:bg-gray-50 active:bg-gray-100 rounded">
                                                {opt}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="text-[11px] text-gray-500/90 flex justify-end items-center mt-[2px] space-x-[3px] self-end clear-both ml-3">
                                    <span>{time}</span>
                                    {isUser && (
                                        <svg viewBox="0 0 16 15" width="16" height="15">
                                            <path fill="#53bdeb" d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        </div>
                    ));
                })}
            </div>

            {/* Input mock area (just for look) */}
            <div className="bg-[#f0f2f5] p-2.5 flex items-center z-10 w-full space-x-2">
                <div className="w-[30px] flex justify-center text-gray-500">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M11.999 14.942c2.001 0 3.531-1.53 3.531-3.531V4.35c0-2.001-1.53-3.531-3.531-3.531S8.469 2.35 8.469 4.35v7.061c0 2.001 1.53 3.531 3.53 3.531h.001zM8.969 4.35c0-1.668 1.346-3.031 3.031-3.031s3.031 1.363 3.031 3.031v7.061c0 1.668-1.346 3.031-3.031 3.031s-3.031-1.363-3.031-3.031V4.35zm.575 8.524H8.469c0 1.688.75 3.25 1.969 4.312l.344.281v2.188h2.375v-2.188l.344-.281c1.219-1.062 1.969-2.625 1.969-4.312h-1.075c0 2.458-1.956 4.414-4.414 4.414s-4.414-1.956-4.414-4.414z"></path></svg>
                </div>
                <div className="flex-1 bg-white rounded-lg px-4 py-2 text-[15px] text-gray-400">
                    Type a message
                </div>
            </div>
            
            <style>
            {`
                .custom-scrollbar::-webkit-scrollbar {
                    height: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: rgba(0,0,0,0.1);
                    border-radius: 10px;
                }
            `}
            </style>
        </div>
    );
};

export default WhatsAppChat;
